import React, { lazy, Suspense } from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'
import { Spin } from 'antd'

// Base Layout & Pages
const GeekLayout = lazy(() => import('@/pages/Layout/index.js'))
const Login = lazy(() => import('@/pages/Login/index.js'))

// Campaign Submodules
const CanvassEntry = lazy(() => import('@/pages/Campaign/CanvassEntry.js'))
const MyCanvassRecords = lazy(() => import('@/pages/Campaign/MyCanvassRecords.js'))
const PhoneCanvass = lazy(() => import('@/pages/Campaign/PhoneCanvass.jsx'))
const VoterLookup = lazy(() => import('@/pages/Campaign/VoterLookup.js'))
const GisMapAnalytics = lazy(() => import('@/pages/Campaign/GisMapAnalytics.js'))
const TurfAssign = lazy(() => import('@/pages/Campaign/TurfAssign.js'))

// GOTV Route Operations
const GOTVRoutes = lazy(() => import('@/pages/Campaign/GOTVRoutes.jsx'))
const GOTVRoutesVoted = lazy(() => import('@/pages/Campaign/GOTVRoutesVoted.jsx')) // 🌟 新增：已投票状态筛选的 GOTV 路线规划页面
const EarlyVotingCenter = lazy(() => import('@/pages/Campaign/EarlyVotingCenter.jsx'))

// 🌟 新增：GOTV 短信催票大盘与审计报表组件
const GotvSmsDashboard = lazy(() => import('@/pages/Campaign/GotvSmsDashboard.jsx'))
const GotvSmsReport = lazy(() => import('@/pages/Campaign/GotvSmsReport.jsx'))

const LawnSignManagement = lazy(() => import('@/pages/Campaign/LawnSignManagement.js'))
const CanvasserPerformance = lazy(() => import('@/pages/Campaign/CanvasserPerformance.js'))
const DemographicReports = lazy(() => import('@/pages/Campaign/DemographicReports.js'))
const CanvassingTrends = lazy(() => import('@/pages/Campaign/CanvassingTrends.js'))
const UserHierarchy = lazy(() => import('@/pages/Campaign/UserHierarchy.js'))

const NotFound = lazy(() => import('@/pages/NotFound/index.js'))


// ============================================================
// Auth Guard
// ============================================================

const AuthRoute = ({ children }) => {
  const token = localStorage.getItem('token_key')

  return token
    ? children
    : <Navigate to="/login" replace />
}


// ============================================================
// Suspense Loader Wrapper
// ============================================================

const withSuspense = (Component) => (
  <Suspense
    fallback={
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          paddingTop: 120
        }}
      >
        <Spin
          size="large"
          tip="Loading Campaign Module..."
        />
      </div>
    }
  >
    {Component}
  </Suspense>
)


// ============================================================
// Router
// ============================================================

const router = createBrowserRouter([

  {
    path: '/',

    element: (
      <AuthRoute>
        {withSuspense(<GeekLayout />)}
      </AuthRoute>
    ),

    children: [

      // ------------------------------------------------------
      // Default
      // ------------------------------------------------------

      {
        index: true,
        element: withSuspense(<CanvassEntry />)
      },


      // ------------------------------------------------------
      // Canvassing
      // ------------------------------------------------------

      {
        path: 'canvass-entry',
        element: withSuspense(<CanvassEntry />)
      },

      {
        path: 'phone-canvass',
        element: withSuspense(<PhoneCanvass />)
      },

      {
        path: 'my-records',
        element: withSuspense(<MyCanvassRecords />)
      },


      // ------------------------------------------------------
      // Voters
      // ------------------------------------------------------

      {
        path: 'voter-search',
        element: withSuspense(<VoterLookup />)
      },


      // ------------------------------------------------------
      // GIS / Map
      // ------------------------------------------------------

      {
        path: 'gis-map',
        element: withSuspense(<GisMapAnalytics />)
      },


      // ------------------------------------------------------
      // Turf Assignment
      // ------------------------------------------------------

      {
        path: 'turf-assign',
        element: withSuspense(<TurfAssign />)
      },


      // ------------------------------------------------------
      // GOTV Route Operations
      // ------------------------------------------------------

      {
        path: 'gotv-routes',
        element: withSuspense(<GOTVRoutes />)
      },

      // 🌟 新增路由：选举日已投票状态过滤路线规划页面
      {
        path: 'gotv-voted-routes',
        element: withSuspense(<GOTVRoutesVoted />)
      },


      // ------------------------------------------------------
      // Lawn Signs
      // ------------------------------------------------------

      {
        path: 'lawn-signs',
        element: withSuspense(<LawnSignManagement />)
      },


      // ------------------------------------------------------
      // Performance
      // ------------------------------------------------------

      {
        path: 'canvassers',
        element: withSuspense(<CanvasserPerformance />)
      },


      // ------------------------------------------------------
      // Reports
      // ------------------------------------------------------

      {
        path: 'reports/demographics',
        element: withSuspense(<DemographicReports />)
      },

      {
        path: 'reports/trends',
        element: withSuspense(<CanvassingTrends />)
      },

      {
        path: 'reports/early-voting',
        element: withSuspense(<EarlyVotingCenter />)
      },

      // 🌟 新增路由：GOTV 短信大盘与审计报表
      {
        path: 'gotv-sms-dashboard',
        element: withSuspense(<GotvSmsDashboard />)
      },

      {
        path: 'gotv-sms-report',
        element: withSuspense(<GotvSmsReport />)
      },


      // ------------------------------------------------------
      // Users
      // ------------------------------------------------------

      {
        path: 'users',
        element: withSuspense(<UserHierarchy />)
      }

    ]
  },


  // ==========================================================
  // Login
  // ==========================================================

  {
    path: '/login',
    element: withSuspense(<Login />)
  },


  // ==========================================================
  // 404
  // ==========================================================

  {
    path: '*',
    element: withSuspense(<NotFound />)
  }

])

export default router